/**
 * Notification System Configuration Constants
 * 
 * This file contains all configurable values for the notification system
 * to allow easy adjustment of behavior without modifying core logic.
 */

// Time constants (in milliseconds)
export const NOTIFICATION_CONSTANTS = {
  // Auto-refresh interval for fetching notifications (2 hours)
  AUTO_REFRESH_INTERVAL: 30 * 1000, // 30 seconds
  
  // How long to keep read notifications visible (3 days)
  READ_NOTIFICATION_RETENTION_DAYS: 3,
  
  // Maximum notifications to fetch in a single query
  MAX_NOTIFICATIONS_PER_FETCH: 50,
  
  // Toast notification display duration (3 seconds)
  TOAST_DURATION: 3000,
  
  // Notification panel slide animation duration (300ms)
  PANEL_ANIMATION_DURATION: 300,
  
  // Polling interval for real-time updates (disabled by default, using 2-hour refresh)
  REAL_TIME_POLLING_INTERVAL: null,
  
  // Auto-mark as read delay when notification is clicked (immediate)
  AUTO_MARK_READ_DELAY: 0,
  
  // Batch size for bulk operations
  BULK_OPERATION_BATCH_SIZE: 10,
} as const;

// Notification priorities
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

// Notification types
export const NOTIFICATION_TYPES = {
  SALES_ORDER_CREATED: 'sales_order_created',
  SALES_ORDER_UPDATED: 'sales_order_updated',
  SALES_ORDER_DELETED: 'sales_order_deleted',
  SALES_ORDER_APPROVED: 'sales_order_approved',
  SALES_ORDER_APPROVER: 'sales_order_approver',
  SALES_ORDER_FYI: 'sales_order_fyi',
  DELIVERY_PAYMENT: 'delivery_payment',
  SYSTEM_UPDATE: 'system_update',
  URGENT_ACTION: 'urgent_action',
  DRIVER_CHECKIN: 'driver_checkin',
  VEHICLE_MAINTENANCE: 'vehicle_maintenance',
  BILLING_ALERT: 'billing_alert',
  SYSTEM: 'system',
} as const;

// UI Configuration
export const NOTIFICATION_UI_CONFIG = {
  // Badge configuration
  BADGE: {
    MAX_COUNT_DISPLAY: 99, // Show "99+" for counts above this
    COLORS: {
      UNREAD: 'red',
      READ: 'gray',
    },
  },
  
  // Panel configuration
  PANEL: {
    WIDTH: {
      MOBILE: '100vw',
      DESKTOP: '400px',
    },
    MAX_HEIGHT: '100vh',
    Z_INDEX: 1000,
  },
  
  // Priority colors for UI display
  PRIORITY_COLORS: {
    [NOTIFICATION_PRIORITIES.LOW]: 'gray',
    [NOTIFICATION_PRIORITIES.NORMAL]: 'blue',
    [NOTIFICATION_PRIORITIES.HIGH]: 'orange',
    [NOTIFICATION_PRIORITIES.URGENT]: 'red',
  },
  
  // Type icons (using Chakra UI icon names)
  TYPE_ICONS: {
    [NOTIFICATION_TYPES.SALES_ORDER_CREATED]: 'AddIcon',
    [NOTIFICATION_TYPES.SALES_ORDER_UPDATED]: 'EditIcon',
    [NOTIFICATION_TYPES.SALES_ORDER_DELETED]: 'DeleteIcon',
    [NOTIFICATION_TYPES.SALES_ORDER_APPROVED]: 'CheckIcon',
    [NOTIFICATION_TYPES.SALES_ORDER_APPROVER]: 'StarIcon',
    [NOTIFICATION_TYPES.SALES_ORDER_FYI]: 'InfoIcon',
    [NOTIFICATION_TYPES.DELIVERY_PAYMENT]: 'AttachmentIcon',
    [NOTIFICATION_TYPES.SYSTEM_UPDATE]: 'SettingsIcon',
    [NOTIFICATION_TYPES.URGENT_ACTION]: 'WarningIcon',
    [NOTIFICATION_TYPES.DRIVER_CHECKIN]: 'TimeIcon',
    [NOTIFICATION_TYPES.VEHICLE_MAINTENANCE]: 'RepeatIcon',
    [NOTIFICATION_TYPES.BILLING_ALERT]: 'InfoIcon',
    [NOTIFICATION_TYPES.SYSTEM]: 'InfoIcon',
  },
} as const;

// Type display names
export const NOTIFICATION_TYPE_DISPLAY_NAMES = {
  [NOTIFICATION_TYPES.SALES_ORDER_CREATED]: 'Sales Order Created',
  [NOTIFICATION_TYPES.SALES_ORDER_UPDATED]: 'Sales Order Updated',
  [NOTIFICATION_TYPES.SALES_ORDER_DELETED]: 'Sales Order Deleted',
  [NOTIFICATION_TYPES.SALES_ORDER_APPROVED]: 'Sales Order Approved',
  [NOTIFICATION_TYPES.SALES_ORDER_APPROVER]: 'Approver Assignment',
  [NOTIFICATION_TYPES.SALES_ORDER_FYI]: 'Auto-Approved',
  [NOTIFICATION_TYPES.DELIVERY_PAYMENT]: 'Delivery Payment',
  [NOTIFICATION_TYPES.SYSTEM_UPDATE]: 'System Update',
  [NOTIFICATION_TYPES.URGENT_ACTION]: 'Urgent Action',
  [NOTIFICATION_TYPES.DRIVER_CHECKIN]: 'Driver Check-in',
  [NOTIFICATION_TYPES.VEHICLE_MAINTENANCE]: 'Vehicle Maintenance',
  [NOTIFICATION_TYPES.BILLING_ALERT]: 'Billing Alert',
  [NOTIFICATION_TYPES.SYSTEM]: 'System',
} as const;

// Feature flags for experimental features
export const NOTIFICATION_FEATURE_FLAGS = {
  // Enable real-time push notifications (when implemented)
  ENABLE_PUSH_NOTIFICATIONS: false,
  
  // Enable notification grouping by type
  ENABLE_NOTIFICATION_GROUPING: false,
  
  // Enable bulk actions (mark all as read, delete all, etc.)
  ENABLE_BULK_ACTIONS: false,
  
  // Enable notification preferences per user
  ENABLE_USER_PREFERENCES: false,
  
  // Enable notification sound/vibration
  ENABLE_NOTIFICATION_SOUND: false,
  
  // Enable notification quick actions (approve/reject directly from panel)
  ENABLE_QUICK_ACTIONS: false,
} as const;

// Helper functions
export const getRetentionCutoffDate = (): Date => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - NOTIFICATION_CONSTANTS.READ_NOTIFICATION_RETENTION_DAYS);
  return cutoff;
};

export const formatNotificationCount = (count: number): string => {
  return count > NOTIFICATION_UI_CONFIG.BADGE.MAX_COUNT_DISPLAY 
    ? `${NOTIFICATION_UI_CONFIG.BADGE.MAX_COUNT_DISPLAY}+` 
    : count.toString();
};

export type NotificationType = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];
export type NotificationPriority = typeof NOTIFICATION_PRIORITIES[keyof typeof NOTIFICATION_PRIORITIES];
